// Aba "Backup" do painel Tecnologia — snapshots restic, espelho no Google Drive
// e download de partes de um snapshot. Backend: GET /api/system/backups (proxy
// admin → twms-metrics-exporter, que roda como `cleverson`, dono de ~/backups).
//
// NÃO restaura nada: só lista e baixa. Restauração é o runbook RESTORE.md, que
// está entre os itens baixáveis aqui.
import { useState, useEffect, useCallback } from 'react'
import {
  FiArchive, FiRefreshCw, FiDownload, FiCloud, FiCloudOff, FiCheckCircle, FiAlertCircle,
  FiClock, FiLoader, FiHardDrive,
} from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import { fmtBytes } from '../hooks/useSystemMetrics'
import { BackupSection } from './BackupSection'

interface Snapshot { id: string; short_id: string; time: string; size_b: number | null; files: number | null; tags: string[]; in_drive: boolean | null }
interface DlItem { key: string; label: string; dir: boolean }
interface BackupData {
  ok: boolean; ts: string
  snapshots: Snapshot[]; restic_error: string | null
  repo: { path: string; size_b: number | null; count: number }
  drive: { dest: string; status: string; synced: number; newest: string | null; checked_at: string | null; error: string | null }
  timer: { next: string | null; last: string | null }
  last_run: { result: string | null; exit_status: number | null; finished: string | null }
  items: DlItem[]
}

// O timer/serviço vêm no formato do systemd ("Wed 2026-07-22 03:30:36 -04"), que
// não é ISO — se o navegador não parsear, mostra o texto cru em vez de "Invalid Date".
const fmtWhen = (s: string | null) => {
  if (!s) return '—'
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function Stat({ icon, label, value, valueClass, sub }: {
  icon: React.ReactNode; label: string; value: string; valueClass?: string; sub?: string
}) {
  return (
    <div className="glass-panel neu-card rounded-2xl p-4 flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{icon}{label}</span>
      <span className={`text-lg font-semibold leading-tight ${valueClass || 'text-foreground'}`}>{value}</span>
      {sub && <span className="text-[11px] text-muted-foreground truncate" title={sub}>{sub}</span>}
    </div>
  )
}

// Selo de sincronia com o Drive. `null` = ainda verificando (a listagem no Drive
// leva ~10s e roda em background no host) — nunca afirmar "não sincronizado" sem saber.
function DriveSeal({ v }: { v: boolean | null }) {
  if (v === null) return <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Consultando o Google Drive…"><FiLoader className="w-3 h-3 animate-spin" /> verificando</span>
  if (v) return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400" title="Este snapshot já está espelhado no Google Drive"><FiCloud className="w-3 h-3" /> no Drive</span>
  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400" title="Existe só no disco do servidor — ainda não foi espelhado no Drive"><FiCloudOff className="w-3 h-3" /> só local</span>
}

export function BackupPanel() {
  const { authenticatedFetch, token, user } = useAuth()
  const [data, setData] = useState<BackupData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [open, setOpen] = useState<string | null>(null)     // snapshot com o menu de download aberto
  const [busy, setBusy] = useState<string | null>(null)      // "<snap>:<item>" em download

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.SYSTEM_BACKUPS, { timeout: 90000 } as RequestInit)
      if (r.status === 403) { setForbidden(true); return }
      const d = await r.json()
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setData(d)
    } catch (e) { setError((e as Error).message) }
    finally { setLoading(false) }
  }, [authenticatedFetch])
  useEffect(() => { load() }, [load])

  // fetch DIRETO, não authenticatedFetch, por dois motivos: ele reenvia GET que
  // falha com 5xx até 3 vezes (seriam três `restic dump` de 500 MB disparados no
  // host por um erro só), e desloga o usuário do CRM inteiro em qualquer 401.
  const download = async (snap: string, item: DlItem) => {
    const key = `${snap}:${item.key}`
    setBusy(key); setError(null)
    try {
      const url = `${API_ENDPOINTS.SYSTEM_BACKUPS_DOWNLOAD}?snapshot=${encodeURIComponent(snap)}&item=${encodeURIComponent(item.key)}`
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      const dispo = r.headers.get('Content-Disposition') || ''
      const m = /filename="([^"]+)"/.exec(dispo)
      const blob = await r.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href; a.download = m ? m[1] : `${item.key}-${snap}${item.dir ? '.zip' : ''}`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(href), 1000)
    } catch (e) { setError(`Falha ao baixar ${item.label}: ${(e as Error).message}`) }
    finally { setBusy(null) }
  }

  const lastOk = data?.last_run.result === 'success'

  if (forbidden) return (
    <div className="h-full flex items-center justify-center text-muted-foreground">
      <div className="text-center"><FiArchive className="w-7 h-7 mx-auto mb-2 opacity-40" /><p className="text-sm">Painel restrito a administradores.</p></div>
    </div>
  )

  return (
    // Nada de overflow no painel: cabeçalho e stats ficam fixos e só a lista de
    // snapshots rola, dentro do próprio card.
    <div className="h-full min-h-0 flex flex-col overflow-y-auto">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-4 flex-1 min-h-0">
        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <FiArchive className="w-5 h-5 text-primary" />
            <h2 className="text-base font-bold text-foreground">Backup</h2>
            <span className="text-xs text-muted-foreground hidden sm:inline">restic diário + espelho no Google Drive</span>
          </div>
          <button onClick={load} disabled={loading} title="Atualizar"
            className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-50">
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-700 dark:text-red-300">{error}</div>
        )}
        {data?.restic_error && (
          <div className="px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300">
            Não deu pra ler os snapshots: {data.restic_error}
          </div>
        )}
        {loading && !data && (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground text-sm">
            <FiLoader className="w-4 h-4 animate-spin" /> Lendo o repositório de backup…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
              <Stat icon={lastOk ? <FiCheckCircle className="w-3.5 h-3.5" /> : <FiAlertCircle className="w-3.5 h-3.5" />}
                label="Última rodada" value={lastOk ? 'Sucesso' : (data.last_run.result || 'desconhecida')}
                valueClass={lastOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
                sub={`terminou ${fmtWhen(data.last_run.finished)}`} />
              <Stat icon={<FiClock className="w-3.5 h-3.5" />} label="Próxima" value={fmtWhen(data.timer.next)}
                sub={`anterior: ${fmtWhen(data.timer.last)}`} />
              <Stat icon={<FiCloud className="w-3.5 h-3.5" />} label="Google Drive"
                value={data.drive.status === 'ok' ? `${data.drive.synced}/${data.repo.count}` : (data.drive.status === 'checking' ? 'verificando…' : 'erro')}
                valueClass={data.drive.status === 'ok' && data.drive.synced >= data.repo.count ? 'text-emerald-600 dark:text-emerald-400' : undefined}
                sub={data.drive.error || `espelhados em ${data.drive.dest}`} />
              <Stat icon={<FiHardDrive className="w-3.5 h-3.5" />} label="Repositório"
                value={fmtBytes(data.repo.size_b)} sub={`${data.repo.count} snapshots em disco`} />
            </div>

            {/* min-h é piso, não altura fixa: em tela baixa o card para de
                encolher e o painel volta a rolar, em vez de espremer 12
                snapshots numa fresta. */}
            <div className="glass-panel neu-card rounded-2xl p-5 flex flex-col gap-3 flex-1 min-h-[300px]">
              <div className="flex items-center justify-between shrink-0">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Snapshots</span>
                <span className="text-[11px] text-muted-foreground">retenção: 7 diários · 4 semanais · 6 mensais</span>
              </div>

              <ul className="divide-y divide-border/40 flex-1 min-h-0 overflow-y-auto pr-1">
                {data.snapshots.map(s => (
                  <li key={s.id} className="py-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <code className="text-[11px] font-mono text-primary shrink-0 w-[70px]" title={s.id}>{s.short_id}</code>
                      <span className="text-[12px] text-foreground shrink-0 w-[110px] tabular-nums">{fmtWhen(s.time)}</span>
                      <span className="text-[11px] text-muted-foreground tabular-nums shrink-0 w-[60px] hidden sm:block"
                        title={s.files ? `${s.files} arquivos` : undefined}>{fmtBytes(s.size_b)}</span>
                      <span className="shrink-0"><DriveSeal v={s.in_drive} /></span>
                      <button onClick={() => setOpen(o => o === s.id ? null : s.id)}
                        title="Baixar uma parte deste snapshot"
                        className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors shrink-0 ${
                          open === s.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-foreground border-border hover:bg-secondary/80'
                        }`}>
                        <FiDownload className="w-3 h-3" /> Baixar
                      </button>
                    </div>

                    {open === s.id && (
                      <div className="mt-2 pl-1 flex flex-wrap gap-1.5">
                        {data.items.map(it => {
                          const k = `${s.short_id}:${it.key}`
                          return (
                            <button key={it.key} onClick={() => download(s.short_id, it)} disabled={busy === k}
                              title={it.dir ? `Baixa ${it.label} como ZIP` : `Baixa ${it.label}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] bg-secondary text-foreground border border-border hover:bg-secondary/80 disabled:opacity-60">
                              {busy === k ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiDownload className="w-3 h-3" />}
                              {it.label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              <div className="text-[10px] text-muted-foreground/70 leading-relaxed border-t border-border/40 pt-2 shrink-0">
                Os pacotes de stack incluem o <code className="text-[9px] bg-secondary/60 px-1 rounded">.env</code> com
                credenciais — armazene offline. Os dumps MySQL passam de 500 MB e o download monta o arquivo
                inteiro na memória do navegador antes de salvar.
              </div>
            </div>

            {/* Gate explícito: o BackupSection gera um ZIP com .env e tokens Meta.
                Antes ele vivia dentro de um {user?.isAdmin && ...} no SistemaPanel;
                ao mudar de lugar, o gate tinha que vir junto. */}
            {user?.isAdmin && <div className="glass-panel neu-card rounded-2xl overflow-hidden shrink-0"><BackupSection /></div>}
          </>
        )}
      </div>
    </div>
  )
}
